import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useClinicalTrials, useMasterData } from "../Hooks";
import Card from "../../../components/Cards/Card";
import { ClinicalTrialsFilters } from "../../../components/ClinicalTrials/ClinicalTrialsFilters";
import { Next } from "../../../components/Icons/Icons";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { RegulatoryEventsContent } from "../../../components/ClinicalTrials/Regulatory/RegulatoryEventsContent";

export const Regulatory = () => {
  const { t } = useTranslation();
  const [indicationOptions, setindicationOptions] = useState();

  const [masterData, isLoadingMasterData] = useMasterData();
  const [
    data,
    query,
    totalSize,
    isLoading,
    handleTableChange,
    handleFilterChange,
  ] = useClinicalTrials({ regulatorySubmissionsOnly: true });

  const columns = [
    {
      dataField: "laboratory",
      text: t("clinicalTrials.startup.sponsor"),
      headerStyle: { width: "100px" },
      formatter: (cell) => cell?.title ?? "-",
    },
    {
      dataField: "protocol.title",
      text: t("clinicalTrials.startup.acronym"),
      headerStyle: { width: "200px" },
    },
    {
      dataField: "protocol.specialty.description",
      text: t("clinicalTrials.startup.specialty"),
      sort: true,
      headerStyle: { width: "200px" },
    },
    {
      dataField: "indication.description",
      text: t("clinicalTrials.brief.associatedIndications"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "keywords",
      text: t("clinicalTrials.startup.keywords"),
      headerStyle: { width: "100px" },
      formatter: (keywords) => {
        if (!Array.isArray(keywords) || keywords.length === 0) return "-";
        return keywords.map((k) => k.description).join(", ");
      },
    },
    {
      dataField: "principalInvestigator",
      text: t("clinicalTrials.startup.principalInvestigator"),
      headerStyle: { width: "100px" },
      formatter: (cell) => cell?.fullName ?? "-",
    },
    {
      dataField: "status.title",
      text: t("clinicalTrials.startup.status"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "version",
      text: t("clinicalTrials.startup.version"),
      headerStyle: { width: "50px" },
    },
    {
      dataField: "view",
      text: "",
      isDummyField: true,
      headerClasses: "header-protocol-actions",
      classes: "row-protocol-actions",
      headerStyle: { width: "100px" },
      headerAlign: "center",
      align: "center",
      formatter: (_cell, row, rowIndex) => (
        <div key={rowIndex}>
          <Tooltip tooltip="Ver más" id={`tooltipvisit-${row.id}`}>
            <NavLink
              to={`/admin/clinicalTrials/edit/${row.id}`}
              activeClassName="active"
              className="btn-table-actions"
            >
              <Next />
            </NavLink>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!masterData.Indications || indicationOptions) {
      return;
    }

    masterData.Indications.forEach((i) => {
      i["title"] = i.description;
    });

    if (masterData.Specialty) {
      masterData.Specialty.forEach((s) => {
        s["title"] = s.description;
      });
    }

    setindicationOptions(masterData.Indications);
  }, [masterData]);

  const expandedContent = (row) => <RegulatoryEventsContent data={row} />;

  return (
    <>
      <Card
        header={
          <Row>
            <ClinicalTrialsFilters
              isLoading={isLoadingMasterData}
              masterData={masterData}
              onChangeFilter={handleFilterChange}
            />
          </Row>
        }
        content={
          <BootstrapTable
            bordered={false}
            hover
            remote
            keyField="id"
            data={data}
            columns={columns}
            classes="table-spaced table-spaced-small"
            wrapperClasses="table-responsive"
            expandRow={{
              renderer: expandedContent,
              onlyOneExpanding: true,
            }}
            pagination={paginationFactory({
              page: query.pagination.page,
              sizePerPage: query.pagination.sizePerPage,
              totalSize: totalSize,
              hideSizePerPage: true,
            })}
            onTableChange={handleTableChange}
            noDataIndication={() => <>{t("commons.noData")}</>}
            loading={isLoading}
            overlay={overlayFactory({ spinner: true })}
          />
        }
      />
    </>
  );
};
